import { BusinessObjectBase, decodeDecimals } from './base';
import { groupMatchSchema } from '@schemas/business-objects/group-match';
import type { GroupMatchEntry } from './group-match-entry';
import type { Horse } from './horse';
import type { HorseGroup } from './horse-group';
import type { User } from './user';
import type { Id, Influence } from '../base-types';
import { pdfRenderResultSchema } from '@schemas/interface-definitions/pdf-render-result';
import type { PdfRenderResult } from '../interface-definitions/index';
import type { Match } from './match';

export interface GroupMatchData {
  readonly id: Id;
  readonly groupName: string;
  readonly mareSizeInfluence: Influence;
  readonly sirePatternInfluence: Influence;
  readonly seed?: number;
  readonly runTimestamp: Date;
  readonly legacyId?: number;
  readonly horseId: Id;
  readonly horse?: Horse;
  readonly groupId: Id;
  readonly group?: HorseGroup;
  readonly runById: Id;
  readonly runBy?: User;
  readonly entries?: GroupMatchEntry[];
}

export interface GroupMatch extends Readonly<GroupMatchData> {}
type GroupMatchCreateWithChildrenData = Partial<
  Omit<GroupMatchData, 'entries'>
> & { entries?: Partial<GroupMatchEntry>[] };

export class GroupMatch extends BusinessObjectBase {
  static readonly entityName = 'GroupMatch' as const;
  static readonly plural = 'group-matches';
  static schema = groupMatchSchema;

  // --- Static CRUD methods ---

  static async find(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<GroupMatch[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params, undefined, options);
    return results.map((data) => new GroupMatch(data));
  }

  static async findOne(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<GroupMatch | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return data ? new GroupMatch(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<GroupMatch> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return new GroupMatch(data);
  }

  static async create(data: Omit<GroupMatchData, 'id'>): Promise<GroupMatch> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new GroupMatch(result);
  }

  static async createMany(
    data: Omit<GroupMatchData, 'id'>[],
  ): Promise<GroupMatch[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new GroupMatch(item));
  }

  static async createWithChildren(
    data: GroupMatchCreateWithChildrenData,
  ): Promise<GroupMatch> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new GroupMatch(result);
  }

  static async createManyWithChildren(
    data: GroupMatchCreateWithChildrenData[],
  ): Promise<GroupMatch[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new GroupMatch(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<GroupMatchData>,
  ): Promise<GroupMatch> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new GroupMatch(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async consolidatedPdf(): Promise<PdfRenderResult> {
    const url = `${BusinessObjectBase.baseUrl}/${GroupMatch.plural}/${(this as any).id}/consolidated-pdf`;
    return BusinessObjectBase.get<PdfRenderResult>(url).then((value) =>
      decodeDecimals(pdfRenderResultSchema, value),
    );
  }
  static async countMatching(where?: Record<string, any>): Promise<number> {
    const url = `${this.baseUrl}/${this.plural}/count-matching`;
    return this.post<number>(url, { where });
  }
  async reportPdf(): Promise<PdfRenderResult> {
    const url = `${BusinessObjectBase.baseUrl}/${GroupMatch.plural}/${(this as any).id}/report-pdf`;
    return BusinessObjectBase.get<PdfRenderResult>(url).then((value) =>
      decodeDecimals(pdfRenderResultSchema, value),
    );
  }
  static async run(
    horseId: number,
    groupId: number,
    mareSizeInfluence: number,
    sirePatternInfluence: number,
    seed?: number,
  ): Promise<GroupMatch> {
    const url = `${this.baseUrl}/${this.plural}/run`;
    return this.post<GroupMatch>(url, {
      horseId,
      groupId,
      mareSizeInfluence,
      sirePatternInfluence,
      seed,
    }).then((value) => decodeDecimals(groupMatchSchema, value));
  }
  async summaryPdf(): Promise<PdfRenderResult> {
    const url = `${BusinessObjectBase.baseUrl}/${GroupMatch.plural}/${(this as any).id}/summary-pdf`;
    return BusinessObjectBase.get<PdfRenderResult>(url).then((value) =>
      decodeDecimals(pdfRenderResultSchema, value),
    );
  }
}
